import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { useNotify, useRedirect } from "react-admin";
import { hostname } from "../../../providers/api";
import LoadingButton from "./LoadingButton";

const errorMessage = async (response) => {
  try {
    const body = await response.json();
    return body?.message || response.statusText;
  } catch {
    return response.statusText;
  }
};

// Deep-copies the project (POST /projects/:id/duplicate) and opens the copy.
const DuplicateProjectButton = ({ record, accessToken }) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  if (!record) return null;

  const openDialog = () => {
    setName(`${record.name} (copy)`);
    setOpen(true);
  };

  const close = () => {
    if (!loading) setOpen(false);
  };

  const duplicate = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${hostname}/projects/${record.id}/duplicate`, {
        method: "POST",
        headers: {
          authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name: name.trim() || undefined }),
      });
      if (!response.ok) {
        notify(`Error duplicating project: ${await errorMessage(response)}`, "error", {
          autoHideDuration: 12000,
        });
        return;
      }
      const copy = await response.json();
      setOpen(false);
      notify(`Project duplicated as "${copy.name}"`, "success");
      redirect(`/projects/${copy.id}/show`);
    } catch (error) {
      notify(`Error duplicating project: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button color="primary" size="small" startIcon={<FileCopyIcon />} onClick={openDialog}>
        Duplicate
      </Button>
      <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle>Duplicate project</DialogTitle>
        <DialogContent>
          <TextField
            id="duplicate-project-name"
            label="New project name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={loading}
            fullWidth
            autoFocus
            margin="dense"
          />
          <DialogContentText style={{ marginTop: 16 }}>
            Copies all articles with their materials, suppliers, processes, packaging and
            calculated impacts (except Ecobalyse) into a new project of the same account.
            Each copied article consumes one account reference.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={loading}>
            Cancel
          </Button>
          <LoadingButton color="primary" variant="contained" onClick={duplicate} loading={loading}>
            Duplicate
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DuplicateProjectButton;
